import { Button } from '@/components/ui/button';
import { TabBar } from '@/components/ui/TabBar';
import { Camera, FolderUp, X, MessageSquare } from 'lucide-react';
import { useRef, type ReactNode } from 'react';
import { useT } from '@/lib/i18n';

// ============================================================================
// ObjectFormWindowHeader — шапка окна формы (topBar UniversalWindow):
// кнопки Сохранить/Провести/Закрыть/«Ещё» (отмена проведения) + actionBar
// (команды макета) + кнопка обсуждения (бейдж непрочитанных) + линия проведённости
// + вкладки (Основное/подчинённые/Задачи/Файлы). Вынесено из AutoObjectForm.
// ============================================================================

interface HeaderTab {
  id: string;
  label: string;
}

interface ActionBtn {
  label: string;
  command?: string;
  onClick?: string;
  variant?: string;
  enabled?: boolean;
  readOnly?: boolean;
}

interface ObjectFormWindowHeaderProps {
  formTitle?: string;
  noObjectRight: boolean;
  objectId?: string | null;
  isDocument: boolean;
  saving: boolean;
  loading: boolean;
  hasLockConflict: boolean;
  formReadOnly: boolean;
  formEnabled: boolean;
  isPosted: boolean;
  actionBar: ActionBtn[];
  tabs: HeaderTab[];
  activeTab: string;
  chatUnread: number;
  moreOpen: boolean;
  onMoreToggle: () => void;
  onClose: () => void;
  onSave: () => void;
  onSaveAndPost: (andClose: boolean) => void;
  onUnpost: () => void;
  onRunCommand: (command: string) => void;
  /** Запись ПОМЕЧЕНА на удаление (показывать «Восстановить» в «Ещё»). */
  markedRecord?: boolean;
  /** Админ: «Удалить окончательно» (деструктив; только в меню «Ещё»). */
  canDeletePermanent?: boolean;
  /** Существующая ЗАДАЧА: «Передать» в «Ещё» (диалог выбора исполнителя;
   *  право проверяет сервер — task:edit/админ). */
  canTransfer?: boolean;
  onOpenChat: () => void;
  onTabChange: (tab: string) => void;
  /** Существующая запись задачи/заявки: стандартные «Сохранить»/«Отмена» скрыты,
   *  на их месте — полоса стандартных команд объекта (commandBar: Выполнено/
   *  Отменить/Удалить). Для новых записей и прочих типов — стандартные кнопки. */
  hideSaveCancel: boolean;
  /** Показывать ли «Сохранить» у существующей задачи/заявки (есть права на
   *  изменение). По умолчанию true; скрывается, если прав нет (noObjectRight). */
  showSave?: boolean;
  /** Полоса стандартных команд объекта (ObjectCommandBar) — слот кнопок шапки. */
  commandBar?: ReactNode;
  /** Кнопка «Печать» (PrintFormButton — печатные формы объекта; сама скрывается,
   *  если форм нет; 1 форма — рендер сразу, >1 — диалог выбора). */
  printButton?: ReactNode;
  /** Кнопка «Позвонить» — ad-hoc видеозвонок из карточки объекта (VideoCallPanel). */
  callButton?: ReactNode;
  /** Объект с вкладкой «Файлы» (в составе «ФайлыПоДокументам») — точки входа
   *  «Загрузить файлы» и «Камера» в ШАПКЕ (не дублируют кнопки вкладки: те же
   *  механизмы — загрузка через chunkedUpload, снимок через CameraCaptureDialog). */
  filesLinked: boolean;
  canAttachFiles: boolean;
  onFilesSelected: (files: File[]) => void;
  onOpenCamera: () => void;
}

export function ObjectFormWindowHeader({
  noObjectRight, objectId, isDocument, saving, loading, hasLockConflict, formReadOnly, formEnabled,
  isPosted, actionBar, tabs, activeTab, chatUnread, moreOpen, onMoreToggle, onClose, onSave,
  onSaveAndPost, onUnpost, onRunCommand, onOpenChat, onTabChange, filesLinked, canAttachFiles,
  onFilesSelected, onOpenCamera, hideSaveCancel, commandBar, showSave = true,
  markedRecord, canDeletePermanent, canTransfer, printButton, callButton,
}: ObjectFormWindowHeaderProps) {
  const t = useT();
  const blocked = saving || loading || hasLockConflict || formReadOnly || !formEnabled || noObjectRight;
  // Скрытый input для «Загрузить файлы» (label-обёртка — надёжно в WebView2)
  const fileInputRef = useRef<HTMLInputElement>(null);
  const hasMore = isDocument || (filesLinked && canAttachFiles) || !!markedRecord || !!canDeletePermanent || !!canTransfer;
  return (
    <div className="flex flex-col gap-1 w-full">
      <div className="flex items-center gap-2 flex-wrap">
        {noObjectRight && (
          <span className="text-[11px] text-muted-foreground border rounded px-2 py-1 bg-muted/40" title={t('form.no_rights_hint')}>
            {objectId ? t('form.no_rights_edit') : t('form.no_rights_create')}
          </span>
        )}
        {isDocument ? (
          <>
            <Button size="sm" onClick={() => onSaveAndPost(true)} disabled={blocked}>{saving ? t('form.posting') : t('form.post_and_close')}</Button>
            <Button size="sm" variant="outline" onClick={() => onSaveAndPost(false)} disabled={blocked}>{t('form.post')}</Button>
            <Button size="sm" variant="outline" onClick={onClose}>{t('common.close')}</Button>
          </>
        ) : hideSaveCancel ? (
          // Существующая задача/заявка: «Сохранить» (если есть права на
          // изменение) + «Отмена» + стандартные команды объекта
          // (Выполнено/Отменить/Удалить — SSoT — сервер, ListStandardCommands).
          <>
            {showSave && (
              <Button size="sm" onClick={onSave} disabled={blocked}>{saving ? t('common.saving') : t('common.save')}</Button>
            )}
            <Button size="sm" variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
            {commandBar}
          </>
        ) : (
          <>
            <Button size="sm" onClick={onSave} disabled={blocked}>{saving ? t('common.saving') : t('common.save')}</Button>
            <Button size="sm" variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
          </>
        )}
        {/* Значок камеры в командной панели (объект с вкладкой «Файлы»): снимок →
            тот же механизм загрузки, что во вкладке (SSoT — CameraCaptureDialog) */}
        {filesLinked && canAttachFiles && (
          <Button size="sm" variant="outline" className="relative" onClick={onOpenCamera} disabled={blocked} title={t('form.camera_hint')}>
            <Camera className="h-3.5 w-3.5" />
          </Button>
        )}
        {actionBar.map((btn, idx) => (
          <Button
            key={idx}
            variant={btn.variant === 'primary' ? 'default' : btn.variant === 'danger' ? 'destructive' : 'outline'}
            size="sm"
            onClick={() => onRunCommand(btn.command || btn.onClick || '')}
            disabled={formReadOnly || !formEnabled || btn.enabled === false || btn.readOnly}
          >
            {btn.label}
          </Button>
        ))}
        {printButton}
        {callButton}
        {objectId && (
          <Button
            size="sm"
            variant="outline"
            className="relative ml-auto"
            onClick={onOpenChat}
            title={t('form.discussion')}
            disabled={formReadOnly || !formEnabled}
          >
            <MessageSquare className="h-3.5 w-3.5" />
            {chatUnread > 0 && (
              <span className="absolute -top-1.5 -right-1.5 text-[9px] leading-none bg-primary text-primary-foreground rounded-full px-1.5 py-1">{chatUnread}</span>
            )}
          </Button>
        )}
        {/* «Ещё»: документы (отмена проведения) + файлы (загрузить) */}
        {hasMore && (
          <div className="relative">
            <Button size="sm" variant="ghost" onClick={onMoreToggle}>{t('common.more')}</Button>
            {moreOpen && (
              <>
                <div className="fixed inset-0 z-[10090]" onClick={onMoreToggle} />
                <div className="absolute right-0 top-full mt-1 z-[10100] min-w-52 rounded-md border bg-popover text-popover-foreground shadow-md py-1">
                  {canTransfer && (
                    <button
                      className="w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 hover:bg-accent/40 disabled:opacity-40 disabled:cursor-not-allowed"
                      disabled={blocked}
                      onClick={() => { onMoreToggle(); onRunCommand('transfer'); }}
                    >
                      {t('form.transfer')}
                    </button>
                  )}
                  {markedRecord && (
                    <button
                      className="w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 hover:bg-accent/40"
                      onClick={() => { onMoreToggle(); onRunCommand('restore'); }}
                    >
                      {t('form.restore')}
                    </button>
                  )}
                  {canDeletePermanent && (
                    <button
                      className="w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 text-destructive hover:bg-destructive/10"
                      onClick={() => { onMoreToggle(); onRunCommand('delete_permanent'); }}
                    >
                      {t('form.delete_permanent')}
                    </button>
                  )}
                  {isDocument && (
                    <button
                      className="w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 hover:bg-accent/40 disabled:opacity-40 disabled:cursor-not-allowed"
                      disabled={saving || !objectId || !isPosted || hasLockConflict || !formEnabled}
                      onClick={() => { onMoreToggle(); onUnpost(); }}
                    >
                      <X className="h-3 w-3" />{t('form.unpost')}
                    </button>
                  )}
                  {filesLinked && canAttachFiles && (
                    <>
                      {/* ЕДИНЫЙ механизм загрузки (chunkedUpload + гео «всегда при
                          возможности») — вызывается родитель; кнопки вкладки НЕ дублируются */}
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        className="hidden"
                        onChange={(e) => {
                          const fs = Array.from(e.target.files || []);
                          e.target.value = '';
                          if (fs.length) { onMoreToggle(); onFilesSelected(fs); }
                        }}
                      />
                      <button
                        className="w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 hover:bg-accent/40 disabled:opacity-40 disabled:cursor-not-allowed"
                        disabled={saving || !objectId || hasLockConflict || !formEnabled}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <FolderUp className="h-3 w-3" />{t('form.upload_files')}
                      </button>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
      {isPosted && <div className="h-0.5 bg-green-600/50 rounded-full" />}
      {tabs.length > 1 && <TabBar tabs={tabs} active={activeTab} onChange={onTabChange} group="object-form" className="w-full" />}
    </div>
  );
}
